"use client"

import { X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import type { Filter, FilterState } from "./filter-section"
import type { JSX } from "react"

interface AppliedFiltersProps {
  filters: FilterState
  filterDefinitions: Filter[]
  onRemove: (filterId: string, optionValue?: string) => void
}

export function AppliedFilters({
  filters,
  filterDefinitions,
  onRemove,
}: AppliedFiltersProps) {
  const getOptionLabel = (filterId: string, optionValue: string) => {
    const filter = filterDefinitions.find((f) => f.id === filterId)
    if (!filter) return optionValue

    const option = filter.options.find((o) => o.value === optionValue)
    return option?.label || optionValue
  }

  const getFilterTitle = (filterId: string) => {
    const filter = filterDefinitions.find((f) => f.id === filterId)
    return filter?.title || filterId
  }

  const getOptionIcon = (filterId: string, optionValue: string) => {
    const filter = filterDefinitions.find((f) => f.id === filterId)
    if (!filter) return null

    const option = filter.options.find((o) => o.value === optionValue)
    return option?.selected
  }

  const renderFilterBadges = () => {
    const badges: JSX.Element[] = []

    Object.entries(filters).forEach(([filterId, value]) => {
      if (Array.isArray(value)) {
        // Handle checkbox/multi-select filters
        value.forEach((optionValue) => {
          const icon = getOptionIcon(filterId, optionValue)

          badges.push(
            <Badge
              key={`${filterId}-${optionValue}`}
              variant="outline"
              className="flex items-center gap-1 font-gilroyRegular text-xs tracking-wide"
            >
              {icon && (
                <img
                  src={icon || "/placeholder.svg"}
                  alt=""
                  className="w-3 h-3 mr-1"
                />
              )}
              <span>
                {getFilterTitle(filterId)}:{" "}
                {getOptionLabel(filterId, optionValue)}
              </span>
              <button
                onClick={() => onRemove(filterId, optionValue)}
                className="ml-1 rounded-full hover:bg-muted p-0.5"
                aria-label={`Remove ${getOptionLabel(
                  filterId,
                  optionValue
                )} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )
        })
      } else if (
        typeof value === "object" &&
        "min" in value &&
        "max" in value
      ) {
        // Handle range filters
        badges.push(
          <Badge
            key={filterId}
            variant="outline"
            className="flex items-center gap-1"
          >
            <span>
              {getFilterTitle(filterId)}: {value.min} - {value.max}
            </span>
            <button
              onClick={() => onRemove(filterId)}
              className="ml-1 rounded-full hover:bg-muted p-0.5"
              aria-label={`Remove ${getFilterTitle(filterId)} filter`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )
      }
    })

    return badges
  }

  return <div className="flex flex-wrap gap-2">{renderFilterBadges()}</div>
}
